import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = document.getElementById('shipping-protection-config');

class ShippingProtectionController {
  constructor(element) {
    this.element = element;
    this.productId = Number(element.dataset.productId);
    this.autoEnable = element.dataset.autoEnable === 'true';
    this.busy = false;
    this.needsSync = false;
    this.ownEvents = new WeakSet();
    this.tiers = [1, 2, 3]
      .map((tier) => ({
        variantId: Number(element.dataset[`tier${tier}VariantId`]),
        threshold: Number(element.dataset[`tier${tier}Threshold`] || 0),
      }))
      .filter((tier) => Number.isInteger(tier.variantId) && tier.variantId > 0)
      .sort((a, b) => a.threshold - b.threshold);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    this.syncFromServer(true);
  }

  get optOutKey() {
    return `shipping-protection-opt-out-${this.productId}`;
  }

  get isOptedOut() {
    return sessionStorage.getItem(this.optOutKey) === 'true';
  }

  set isOptedOut(value) {
    if (value) {
      sessionStorage.setItem(this.optOutKey, 'true');
    } else {
      sessionStorage.removeItem(this.optOutKey);
    }
  }

  onCartLinesUpdate = (event) => {
    if (this.ownEvents.has(event)) return;

    event.promise
      ?.then(() => this.syncFromServer(true))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[shipping-protection] Cart update rejected:', error);
      });
  };

  async fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Unable to read cart: ${response.status}`);
    return response.json();
  }

  getProtectionItem(cart) {
    return cart.items?.find((item) => Number(item.product_id) === this.productId) || null;
  }

  getRealItems(cart) {
    return (cart.items || []).filter((item) => Number(item.product_id) !== this.productId);
  }

  getRealQuantity(cart) {
    return this.getRealItems(cart).reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  getCorrectVariantId(cart) {
    const subtotal = this.getRealItems(cart).reduce(
      (total, item) => total + Number(item.final_line_price || 0),
      0
    );
    let activeTier = this.tiers[0];
    for (const tier of this.tiers) {
      if (subtotal >= tier.threshold) activeTier = tier;
    }
    return activeTier?.variantId || null;
  }

  getSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  buildPayload(payload) {
    const sectionIds = this.getSectionIds();
    if (!sectionIds.length) return payload;
    return {
      ...payload,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  createEvent(action, lines, deferred) {
    const event = new CartLinesUpdateEvent({
      action,
      context: 'shipping-protection',
      lines,
      promise: deferred.promise,
    });
    this.ownEvents.add(event);
    return event;
  }

  resolveEvent(deferred, cart, sections) {
    const standardCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    const cartForEvent = { ...standardCart, totalQuantity: this.getRealQuantity(cart) };
    deferred.resolve({
      cart: cartForEvent,
      detail: {
        sections,
        items: cart.items,
        itemCount: this.getRealQuantity(cart),
        source: 'shipping-protection',
        didError: false,
      },
    });
  }

  async add(variantId) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = this.createEvent('add', [{ merchandiseId: String(variantId), quantity: 1 }], deferred);
    this.element.dispatchEvent(event);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(this.buildPayload({ items: [{ id: variantId, quantity: 1 }] })),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add protection');
      const cart = await this.fetchCart();
      this.resolveEvent(deferred, cart, result.sections);
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  async update(updates, action, lines) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = this.createEvent(action, lines, deferred);
    this.element.dispatchEvent(event);

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(this.buildPayload({ updates })),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Unable to update protection');
      this.resolveEvent(deferred, cart, cart.sections);
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  async remove(item) {
    return this.update({ [item.key]: 0 }, 'remove', [{ id: item.key, quantity: 0 }]);
  }

  async swap(item, variantId) {
    return this.update(
      { [item.key]: 0, [variantId]: 1 },
      'update',
      [
        { id: item.key, quantity: 0 },
        { merchandiseId: String(variantId), quantity: 1 },
      ]
    );
  }

  async setEnabled(enabled) {
    if (this.busy) return;
    this.busy = true;

    try {
      const cart = await this.fetchCart();
      const item = this.getProtectionItem(cart);
      if (enabled) {
        this.isOptedOut = false;
        if (!item && this.getRealQuantity(cart) > 0) {
          const variantId = this.getCorrectVariantId(cart);
          if (variantId) await this.add(variantId);
        }
      } else {
        this.isOptedOut = true;
        if (item) await this.remove(item);
      }
    } finally {
      this.busy = false;
      if (this.needsSync) {
        this.needsSync = false;
        this.syncFromServer(false);
      }
    }
  }

  async syncFromServer(allowAutoAdd) {
    if (this.busy) {
      this.needsSync = true;
      return;
    }

    this.busy = true;
    try {
      const cart = await this.fetchCart();
      const realQuantity = this.getRealQuantity(cart);
      const item = this.getProtectionItem(cart);

      if (realQuantity === 0) {
        this.isOptedOut = false;
        if (item) await this.remove(item);
        return;
      }

      if (!item) {
        if (allowAutoAdd && this.autoEnable && !this.isOptedOut) {
          const variantId = this.getCorrectVariantId(cart);
          if (variantId) await this.add(variantId);
        }
        return;
      }

      const correctVariantId = this.getCorrectVariantId(cart);
      if (correctVariantId && Number(item.variant_id) !== correctVariantId) {
        await this.swap(item, correctVariantId);
      }
    } catch (error) {
      console.warn('[shipping-protection] Synchronization failed:', error);
    } finally {
      this.busy = false;
      if (this.needsSync) {
        this.needsSync = false;
        this.syncFromServer(false);
      }
    }
  }
}

class ShippingProtection extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange = async (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-shipping-protection-toggle]');
    if (!(toggle instanceof HTMLInputElement)) return;

    toggle.disabled = true;
    try {
      await controller?.setEnabled(toggle.checked);
    } catch (error) {
      toggle.checked = !toggle.checked;
      console.warn('[shipping-protection] Toggle failed:', error);
    } finally {
      toggle.disabled = false;
    }
  };
}

const controller = config ? new ShippingProtectionController(config) : null;

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}
